import type { Variants, TargetAndTransition, Transition } from "framer-motion";
import { curves, spring } from "./transitions";

type AnimProps = {
  initial: TargetAndTransition | string;
  animate: TargetAndTransition | string;
  exit?: TargetAndTransition | string;
  transition?: Transition;
  variants?: Variants;
};

export const fade: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.45, ease: curves.expo } },
  exit:    { opacity: 0, transition: { duration: 0.25, ease: "easeIn" } },
};

export function fadeProps(delay = 0): AnimProps {
  return {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.45, ease: curves.expo, delay },
  };
}

export const fadeUp: Variants = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.65, ease: curves.sharp } },
  exit:    { opacity: 0, y: -16, transition: { duration: 0.3, ease: "easeIn" } },
};

export function fadeUpProps(delay = 0, y = 28): AnimProps {
  return {
    initial: { opacity: 0, y },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.65, ease: curves.sharp, delay },
  };
}

export const fadeDown: Variants = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.55, ease: curves.expo } },
  exit:    { opacity: 0, y: -12, transition: { duration: 0.25, ease: "easeIn" } },
};

export const fadeLeft: Variants = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: curves.sharp } },
  exit:    { opacity: 0, x: -12, transition: { duration: 0.25, ease: "easeIn" } },
};

export const fadeRight: Variants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: curves.sharp } },
  exit:    { opacity: 0, x: 12, transition: { duration: 0.25, ease: "easeIn" } },
};

export function fadeLeftProps(delay = 0, x = 20): AnimProps {
  return {
    initial: { opacity: 0, x: -x },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.5, ease: curves.sharp, delay },
  };
}

export function fadeRightProps(delay = 0, x = 20): AnimProps {
  return {
    initial: { opacity: 0, x },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.5, ease: curves.sharp, delay },
  };
}

export const scaleUp: Variants = {
  initial: { opacity: 0, scale: 0.94 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.55, ease: curves.expo } },
  exit:    { opacity: 0, scale: 0.96, transition: { duration: 0.25, ease: "easeIn" } },
};

export const scalePop: Variants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1, transition: spring.bouncy },
  exit:    { opacity: 0, scale: 0.85, transition: { duration: 0.15 } },
};

export const scaleXReveal: Variants = {
  initial: { scaleX: 0, opacity: 0 },
  animate: { scaleX: 1, opacity: 1, transition: { duration: 0.5, ease: curves.sharp } },
  exit:    { scaleX: 0, opacity: 0, transition: { duration: 0.25 } },
};

export function scaleXRevealProps(delay = 0): AnimProps {
  return {
    initial: { scaleX: 0, opacity: 0 },
    animate: { scaleX: 1, opacity: 1 },
    transition: { duration: 0.5, ease: curves.sharp, delay },
  };
}

export const scaleYReveal: Variants = {
  initial: { scaleY: 0, opacity: 0 },
  animate: { scaleY: 1, opacity: 1, transition: { duration: 0.55, ease: curves.expo } },
};

export function staggerContainer(
  staggerChildren = 0.1,
  delayChildren = 0,
  staggerDirection: 1 | -1 = 1
): Variants {
  return {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: { staggerChildren, delayChildren, staggerDirection },
    },
    exit: {
      opacity: 0,
      transition: { staggerChildren: staggerChildren * 0.5, staggerDirection: -1 },
    },
  };
}

export function staggerOrchestrator(
  staggerChildren = 0.1,
  delayChildren = 0
): Variants {
  return {
    initial: {},
    animate: { transition: { staggerChildren, delayChildren } },
    exit:    { transition: { staggerChildren: staggerChildren * 0.5, staggerDirection: -1 } },
  };
}

export const pageTransition: Variants = {
  initial: { opacity: 0, y: 16 },
  animate: {
    opacity: 1, y: 0,
    transition: { duration: 0.45, ease: curves.expo, staggerChildren: 0.07 },
  },
  exit: {
    opacity: 0, y: -8,
    transition: { duration: 0.25, ease: "easeIn" },
  },
};

export const pageFade: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.4, ease: curves.smooth } },
  exit:    { opacity: 0, transition: { duration: 0.25 } },
};

export const pageSlideLeft: Variants = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.4, ease: curves.sharp } },
  exit:    { opacity: 0, x: -40, transition: { duration: 0.3, ease: "easeIn" } },
};

export const pageSlideRight: Variants = {
  initial: { opacity: 0, x: -40 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.4, ease: curves.sharp } },
  exit:    { opacity: 0, x: 40, transition: { duration: 0.3, ease: "easeIn" } },
};

export const drawerLeft: Variants = {
  initial: { x: "-100%", opacity: 0 },
  animate: { x: 0, opacity: 1, transition: spring.stiff },
  exit:    { x: "-100%", opacity: 0, transition: { ...spring.stiff, duration: 0.25 } },
};

export const drawerRight: Variants = {
  initial: { x: "100%", opacity: 0 },
  animate: { x: 0, opacity: 1, transition: spring.stiff },
  exit:    { x: "100%", opacity: 0, transition: { ...spring.stiff, duration: 0.25 } },
};

export const drawerBottom: Variants = {
  initial: { y: "100%", opacity: 0 },
  animate: { y: 0, opacity: 1, transition: spring.stiff },
  exit:    { y: "100%", opacity: 0, transition: { ...spring.stiff, duration: 0.28 } },
};

export const drawerTop: Variants = {
  initial: { y: "-100%", opacity: 0 },
  animate: { y: 0, opacity: 1, transition: spring.stiff },
  exit:    { y: "-100%", opacity: 0, transition: { ...spring.stiff, duration: 0.22 } },
};

export const backdrop: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.25 } },
  exit:    { opacity: 0, transition: { duration: 0.2, delay: 0.05 } },
};

export const modal: Variants = {
  initial: { opacity: 0, scale: 0.93, y: 16 },
  animate: { opacity: 1, scale: 1, y: 0, transition: spring.standard },
  exit:    { opacity: 0, scale: 0.96, y: 8, transition: { duration: 0.2 } },
};

export const dialog: Variants = {
  initial: { opacity: 0, scale: 0.88 },
  animate: { opacity: 1, scale: 1, transition: spring.snappy },
  exit:    { opacity: 0, scale: 0.94, transition: { duration: 0.18 } },
};

export const tapScale = {
  whileTap: { scale: 0.96 },
  transition: spring.snappy,
} as const;

export const hoverLift = {
  whileHover: { y: -2, transition: spring.snappy },
  whileTap:   { y: 0, scale: 0.97, transition: spring.snappy },
} as const;

export const hoverScale = {
  whileHover: { scale: 1.03, transition: spring.snappy },
  whileTap:   { scale: 0.97, transition: spring.snappy },
} as const;

export const hoverGlow = {
  whileHover: { scale: 1.02, transition: spring.gentle },
} as const;

export const arrowNudge: Variants = {
  rest:    { x: 0 },
  hovered: { x: 4, transition: spring.snappy },
};

export function rotateVariant(degrees: number): Variants {
  return {
    closed: { rotate: 0, transition: spring.snappy },
    open:   { rotate: degrees, transition: spring.snappy },
  };
}

export function scrollFadeUp(delay = 0, y = 40): AnimProps {
  return {
    initial: { opacity: 0, y },
    animate: { opacity: 1, y: 0 }, 
    // @ts-expect-error -- spread pattern; consumer uses whileInView
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: curves.expo, delay },
  };
}

export function inViewFadeUp(delay = 0, y = 36) {
  return {
    initial: { opacity: 0, y },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.65, ease: curves.sharp, delay },
  } as const;
}

export function inViewFadeLeft(delay = 0) {
  return {
    initial: { opacity: 0, x: -28 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, ease: curves.sharp, delay },
  } as const;
}

export function inViewFadeRight(delay = 0) {
  return {
    initial: { opacity: 0, x: 28 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, ease: curves.sharp, delay },
  } as const;
}

export function inViewScaleUp(delay = 0) {
  return {
    initial: { opacity: 0, scale: 0.92 },
    whileInView: { opacity: 1, scale: 1 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.6, ease: curves.expo, delay },
  } as const;
}

export const listItem: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: curves.sharp } },
  exit:    { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

export const cardItem: Variants = {
  initial: { opacity: 0, scale: 0.95, y: 14 },
  animate: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.5, ease: curves.expo } },
  exit:    { opacity: 0, scale: 0.96, transition: { duration: 0.2 } },
};

export const badgeItem: Variants = {
  initial: { opacity: 0, scale: 0.7 },
  animate: { opacity: 1, scale: 1, transition: spring.bouncy },
  exit:    { opacity: 0, scale: 0.7, transition: { duration: 0.15 } },
};

export const rowItem: Variants = {
  initial: { opacity: 0, x: -16 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.45, ease: curves.sharp } },
  exit:    { opacity: 0, x: 12, transition: { duration: 0.2 } },
};

export const skeletonToContent: Variants = {
  loading: { opacity: 1 },
  loaded:  { opacity: 0, transition: { duration: 0.3 } },
};

export const contentFromSkeleton: Variants = {
  loading: { opacity: 0 },
  loaded:  { opacity: 1, transition: { duration: 0.4, delay: 0.1 } },
};

export const wordReveal: Variants = {
  initial: { opacity: 0, y: "0.4em", rotateX: -20 },
  animate: {
    opacity: 1, y: 0, rotateX: 0,
    transition: { duration: 0.55, ease: curves.sharp },
  },
};

export const lineReveal: Variants = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.65, ease: curves.sharp } },
};

export const accordionContent: Variants = {
  expanded: {
    height: "auto",
    opacity: 1,
    transition: { height: spring.gentle, opacity: { duration: 0.2, delay: 0.05 } },
  },
  collapsed: {
    height: 0,
    opacity: 0,
    transition: { height: spring.gentle, opacity: { duration: 0.15 } },
  },
};

export const toast: Variants = {
  initial: { opacity: 0, y: -20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1, transition: spring.snappy },
  exit:    { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
};

export const notifDot: Variants = {
  initial: { scale: 0, opacity: 0 },
  animate: { scale: 1, opacity: 1, transition: spring.bouncy },
  exit:    { scale: 0, opacity: 0, transition: { duration: 0.15 } },
};

export const statReveal: Variants = {
  initial: { opacity: 0, y: 12, scale: 0.95 },
  animate: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.6, ease: curves.expo },
  },
};